import React, { useState } from 'react'
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import DeleteIcon from '@mui/icons-material/Delete'
import { type Building } from 'services'
import { createBuilding, deleteBuilding, updateBuilding, type BuildingPayload } from 'services/building/buildingService'
import { AlertDialog, EditActions, IconButtonWithTooltip, isValidUsZipCode, useExecOrgParentLuts, useGlobalMessageHandler } from 'components'
import { formatErrorFieldMessages } from 'interfaces'
import {
  BUILDING_CHAMBER_OPTIONS,
  BUILDING_LEG_TYPES,
  BUILDING_LEG_TYPE_OPTIONS,
  buildingChamberMode,
  federalExecChamberOptions,
  STATE_EXEC_BUILDING_CHAMBER
} from 'helpers'

const legTypeLabel = (code: string | null | undefined): string =>
  BUILDING_LEG_TYPE_OPTIONS.find(o => o.value === code)?.label ?? code ?? '—'

interface BuildingCardProps {
  building: Building
  isNew?: boolean
  onSaved: (building: Building) => void
  onDeleted: (id: number) => void
  onCancelNew?: () => void
}

const emptyPayload = (b: Building): BuildingPayload => ({
  displayName: b.displayName ?? '',
  buildingName: b.buildingName ?? null,
  room: b.room ?? null,
  street: b.street ?? null,
  street2: b.street2 ?? null,
  city: b.city ?? null,
  state: b.state ?? '',
  zipCode: b.zipCode ?? '',
  legType: b.legType ?? '',
  chamber: b.chamber ?? null
})

export const BuildingCard: React.FC<BuildingCardProps> = ({
  building,
  isNew = false,
  onSaved,
  onDeleted,
  onCancelNew
}) => {
  const [expanded, setExpanded] = useState(isNew)
  const [disabled, setDisabled] = useState(!isNew)
  const [formData, setFormData] = useState<BuildingPayload>(emptyPayload(building))
  const [loading, setLoading] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const { showSuccessMessage, showErrorMessage } = useGlobalMessageHandler()
  const { lutValues: parentLut } = useExecOrgParentLuts()

  const chamberMode = buildingChamberMode(formData.legType)

  const chamberSummary = (): string => {
    if (building.legType === BUILDING_LEG_TYPES.STATE_EXECUTIVE) return '—'
    if (building.legType === BUILDING_LEG_TYPES.FEDERAL_EXECUTIVE) {
      const org = parentLut.parents.get(Number(building.chamber))
      return org != null ? `${building.chamber ?? ''} - ${org.name}` : building.chamber ?? '—'
    }
    return BUILDING_CHAMBER_OPTIONS.find(o => o.value === (building.chamber ?? ''))?.label ?? '—'
  }

  const handleField = (field: keyof BuildingPayload) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value !== '' ? e.target.value : null }))
  }

  const handleSelect = (field: keyof BuildingPayload) => (e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value !== '' ? e.target.value : null }))
  }

  const handleLegTypeSelect = (e: SelectChangeEvent): void => {
    const legType = e.target.value
    setFormData(prev => buildingChamberMode(prev.legType) === buildingChamberMode(legType)
      ? { ...prev, legType }
      : { ...prev, legType, chamber: null })
  }

  const handleSave = (): void => {
    if (formData.displayName == null || formData.displayName === '') {
      showErrorMessage('Display name is required')
      return
    }
    if (formData.state == null || formData.state === '') {
      showErrorMessage('State is required')
      return
    }
    if (formData.zipCode == null || formData.zipCode === '') {
      showErrorMessage('Zip code is required')
      return
    }
    if (!isValidUsZipCode(formData.zipCode)) {
      showErrorMessage('Please enter a primary 5-digit or 9-digit zip code.')
      return
    }
    if (formData.legType == null || formData.legType === '') {
      showErrorMessage('Leg type is required')
      return
    }
    if (chamberMode === 'federalExec' && (formData.chamber == null || formData.chamber === '')) {
      showErrorMessage('Organization is required')
      return
    }
    const isExistingStateExec = !isNew && building.legType === BUILDING_LEG_TYPES.STATE_EXECUTIVE
    const payload: BuildingPayload = {
      ...formData,
      chamber: chamberMode === 'none' && !isExistingStateExec
        ? STATE_EXEC_BUILDING_CHAMBER
        : formData.chamber ?? null
    }
    setLoading(true)
    const call = isNew
      ? createBuilding(payload)
      : updateBuilding(building.id, payload)

    void call.then(response => {
      setLoading(false)
      if (response.succeeded) {
        showSuccessMessage(isNew ? 'Building created' : 'Building updated')
        if (isNew && response.result != null) {
          onSaved(response.result)
        } else {
          onSaved({ ...building, ...payload })
        }
        setDisabled(true)
        setExpanded(false)
      } else {
        showErrorMessage(formatErrorFieldMessages(response.errors))
      }
    })
  }

  const handleDelete = (): void => {
    setLoading(true)
    void deleteBuilding(building.id).then(response => {
      setLoading(false)
      if (response.succeeded) {
        showSuccessMessage('Building deleted')
        onDeleted(building.id)
      } else {
        showErrorMessage(formatErrorFieldMessages(response.errors))
      }
    })
  }

  const handleCancel = (): void => {
    if (isNew) {
      onCancelNew?.()
    } else {
      setFormData(emptyPayload(building))
      setDisabled(true)
      setExpanded(false)
    }
  }

  const addressSummary = [building.street, building.city, building.state]
    .filter(Boolean)
    .join(', ')

  return (
    <Card variant="outlined" sx={{ mb: 1, p: 1.5 }}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={'auto'}>
          <IconButtonWithTooltip
            label={expanded ? 'Collapse' : 'Expand'}
            onClick={() => { setExpanded(prev => !prev) }}
            icon={expanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography variant="body2" fontWeight="bold">
            {building.displayName ?? '(new building)'}
          </Typography>
        </Grid>
        <Grid item xs>
          <Typography variant="body2" color="text.secondary">
            {addressSummary !== '' ? addressSummary : '—'}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="body2">{legTypeLabel(building.legType)}</Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Typography variant="body2">{chamberSummary()}</Typography>
        </Grid>
        <Grid item xs={'auto'}>
          {loading
            ? <CircularProgress size={20} />
            : (
            <Box display="flex">
              <EditActions
                entityName="Building"
                editing={!disabled}
                onEdit={() => { setDisabled(false); setExpanded(true) }}
                onCancel={handleCancel}
                onSave={handleSave}
              />
              {disabled && !isNew && (
                <IconButtonWithTooltip label="Delete" color="secondary" onClick={() => { setShowDeleteDialog(true) }} icon={<DeleteIcon />} />
              )}
            </Box>
              )}
        </Grid>
      </Grid>

      {expanded && (
        <>
          <Divider sx={{ my: 1.5, borderColor: 'primary.main' }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Display Name"
                size="small"
                fullWidth
                required
                disabled={disabled}
                value={formData.displayName ?? ''}
                onChange={handleField('displayName')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Building Name"
                size="small"
                fullWidth
                disabled={disabled}
                value={formData.buildingName ?? ''}
                onChange={handleField('buildingName')}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Room"
                size="small"
                fullWidth
                disabled={disabled}
                value={formData.room ?? ''}
                onChange={handleField('room')}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="State"
                size="small"
                fullWidth
                disabled={disabled}
                inputProps={{ maxLength: 2 }}
                value={formData.state ?? ''}
                onChange={handleField('state')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Street"
                size="small"
                fullWidth
                disabled={disabled}
                value={formData.street ?? ''}
                onChange={handleField('street')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Street 2"
                size="small"
                fullWidth
                disabled={disabled}
                value={formData.street2 ?? ''}
                onChange={handleField('street2')}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="City"
                size="small"
                fullWidth
                disabled={disabled}
                value={formData.city ?? ''}
                onChange={handleField('city')}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                label="Zip Code"
                size="small"
                fullWidth
                disabled={disabled}
                value={formData.zipCode ?? ''}
                onChange={handleField('zipCode')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl size="small" fullWidth disabled={disabled}>
                <InputLabel>Leg Type</InputLabel>
                <Select
                  label="Leg Type"
                  value={formData.legType ?? ''}
                  onChange={handleLegTypeSelect}
                >
                  {BUILDING_LEG_TYPE_OPTIONS.map(o => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {chamberMode !== 'none' && (
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth disabled={disabled}>
                  <InputLabel>{chamberMode === 'federalExec' ? 'Organization' : 'Chamber'}</InputLabel>
                  <Select
                    label={chamberMode === 'federalExec' ? 'Organization' : 'Chamber'}
                    value={formData.chamber ?? ''}
                    onChange={handleSelect('chamber')}
                  >
                    {(chamberMode === 'federalExec'
                      ? federalExecChamberOptions(parentLut.parents, formData.chamber)
                      : BUILDING_CHAMBER_OPTIONS
                    ).map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </>
      )}

      <AlertDialog
        open={showDeleteDialog}
        title="Delete Building"
        contentText={`Are you sure you want to delete "${building.displayName ?? ''}"? This cannot be undone.`}
        nameButtonConfirm="Delete"
        nameButtonCancel="Cancel"
        onClose={() => { setShowDeleteDialog(false) }}
        onButtonClick={() => { setShowDeleteDialog(false); handleDelete() }}
      />
    </Card>
  )
}
